import axios from "axios";
import LogRocket from "logrocket";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

import {
  newAnalyticsProxy,
  reset,
  track,
  updateIntercom,
} from "shared/boot/analytics";
import { $streemApiV1 } from "shared/boot/api";
import { appInstance } from "shared/boot/app";
import { setLocale } from "shared/boot/i18n";
import { identityBaseUrl } from "shared/constants";
import {
  cachedRefreshToken,
  cachedToken,
  cacheOrganisationRefreshToken,
  cacheOrganisationToken,
  clearCachedAuthTokens,
  currentRefreshToken,
  currentToken,
  updateUserRefreshToken,
  updateUserToken,
} from "shared/helpers/authorization";
import {
  getInitialCurrentUserState,
  getInitialOrganisationState,
} from "shared/helpers/user";
import { useSideBarStore } from "shared/modules/SideBar/stores/useSideBarStore";
import { OrganisationAggregationMultiplier } from "shared/resources";
import OrganisationsService from "shared/services/api/OrganisationsService";
import UsersService from "shared/services/api/UsersService";
import { clearPendoSession } from "shared/services/pendo";
import Permissions from "shared/services/Permissions";
import StorageService from "shared/services/StorageService";
import { revokeToken, signIn } from "shared/services/tokenService";
import type { Nullable } from "shared/types";

import { useUniversalPlayerStore } from "../universalPlayer";

import type {
  AccountManager,
  AuthToken,
  CurrentUser,
  Organisation,
  OrganisationTeam,
  Preferences,
  SortedOrganisation,
  TeamMember,
  UpdateUser,
} from "./types";

const handleErrors = true;

interface UserOrganisationPayload {
  organisation: {
    id: number;
    logo_url: Nullable<string>;
    name: string;
    teams: OrganisationTeam[];
  };
  user_id: number;
}

interface UserOrganisationsResponse {
  organisations: UserOrganisationPayload[];
  region?: string;
}

export const useUserStore = defineStore("user", () => {
  const accountManager = ref<AccountManager>();

  const betaEnabled = ref<boolean>(
    JSON.parse(sessionStorage.getItem("betaEnabled") || "false")
  );

  const enforceTwoFactor = ref(false);
  const isLoggedIn = ref<boolean>(false);
  const isAdminUser = ref<boolean>(false);
  const loadError = ref<Nullable<unknown>>(null);
  const pending = ref<boolean>(false);
  const preferences = ref<Preferences>({});
  const token = ref<AuthToken>("");
  const identityProvider = ref<Nullable<string>>("");
  const currentUser = ref<CurrentUser>(getInitialCurrentUserState());
  const showFullExcerpts = ref<boolean>(false);
  const customerViewEnabled = ref<boolean>(false);
  const organisation = ref<Organisation>(getInitialOrganisationState());
  const userOrganisations = ref<SortedOrganisation[]>([]);
  const userOrganisationsRegion = ref<string>("");
  const multiplier = ref<Nullable<OrganisationAggregationMultiplier>>(null);
  const team = ref<TeamMember[]>([]);

  const hasAdvancedAccess = computed((): boolean => {
    const permissions = new Permissions();

    return Boolean(
      permissions.has("has_advanced_access") && !customerViewEnabled.value
    );
  });

  const adminUserEnabled = computed((): boolean =>
    Boolean(isAdminUser.value && !customerViewEnabled.value)
  );

  const organisationId = computed(
    (): number | undefined => currentUser.value.organisation?.id
  );

  const role = computed((): string => currentUser.value.role || "");

  const primaryOrganisationTeam = computed((): OrganisationTeam | undefined =>
    currentUser.value.organisation_teams.find(
      (organisationTeam) => organisationTeam.primary
    )
  );

  const sortedUserOrganisationTeams = computed((): OrganisationTeam[] => {
    const primaryOrganisationTeams: OrganisationTeam[] =
      primaryOrganisationTeam.value ? [primaryOrganisationTeam.value] : [];

    const sortedSecondaryTeams: OrganisationTeam[] | undefined =
      currentUser.value.organisation_teams
        .filter((organisationTeam) => !organisationTeam.primary)
        .sort((secondaryTeamA, secondaryTeamB) =>
          secondaryTeamA.name > secondaryTeamB.name ? 1 : -1
        );

    if (sortedSecondaryTeams) {
      return primaryOrganisationTeams.concat(sortedSecondaryTeams);
    }

    return primaryOrganisationTeams;
  });

  const sortedTeam = computed((): TeamMember[] =>
    team.value
      .slice()
      .sort((userA, userB) => (userA.first_name > userB.first_name ? 1 : -1))
  );

  const sortedAllOrganisationTeams = computed((): OrganisationTeam[] => {
    if (!organisation.value || !organisation.value.organisation_teams) {
      return [];
    }

    return organisation.value.organisation_teams.toSorted((teamA, teamB) =>
      teamA.name > teamB.name ? 1 : -1
    );
  });

  const sortedOrganisations = computed<SortedOrganisation[]>(() => {
    if (userOrganisations.value.length) {
      return userOrganisations.value;
    }

    return [
      {
        ...currentUser.value.organisation,
        teams: currentUser.value.organisation_teams?.length
          ? currentUser.value.organisation_teams
          : null,
      },
      ...(currentUser.value.other_organisations || []).map((org) => ({
        ...org,
        teams: null,
      })),
    ];
  });

  const hasLaunchpadBetaEnabled = computed<boolean>(
    () => currentUser.value.launchpad_beta
  );

  function loginSuccess(newToken: AuthToken): void {
    token.value = newToken;
    isLoggedIn.value = true;
    pending.value = false;
  }

  function setEnforceTwoFactor(updateEnforceTwoFactor: boolean): void {
    enforceTwoFactor.value = isAdminUser.value ? false : updateEnforceTwoFactor;
  }

  function login({
    emailOrPhone,
    pin,
    platform,
    twoFactorCode,
  }: {
    emailOrPhone: string;
    pin: string;
    platform?: string;
    twoFactorCode?: string;
  }): Promise<void> {
    pending.value = true;

    return signIn(emailOrPhone, pin, platform, twoFactorCode)
      .then(async (response) => {
        const { access_token: accessToken, refresh_token: newRefreshToken } =
          response;

        useUserStore().loginSuccess(accessToken);

        if ("enforce_two_factor" in response) {
          useUserStore().setEnforceTwoFactor(
            Boolean(response.enforce_two_factor)
          );
        }

        identityProvider.value = "streem";

        StorageService.set("login", emailOrPhone);
        StorageService.set("identityProvider", "streem");

        await clearCachedAuthTokens();

        await updateUserToken(accessToken);
        await updateUserRefreshToken(newRefreshToken);
      })
      .catch((error) => {
        pending.value = false;

        return Promise.reject(error);
      });
  }

  function initialState(): void {
    accountManager.value = undefined;
    betaEnabled.value = false;
    currentUser.value = getInitialCurrentUserState();
    customerViewEnabled.value = false;
    enforceTwoFactor.value = false;
    identityProvider.value = "";
    isAdminUser.value = false;
    isLoggedIn.value = false;
    userOrganisations.value = [];
    userOrganisationsRegion.value = "";
    loadError.value = null;
    multiplier.value = null;
    organisation.value = getInitialOrganisationState();
    pending.value = false;
    preferences.value = {};
    showFullExcerpts.value = false;
    team.value = [];
    token.value = "";
  }

  async function logout(clearAll = false): Promise<void> {
    if (!isAdminUser.value) {
      track("Signed Out");
      reset();
    }

    const refreshToken = await currentRefreshToken();

    if (refreshToken) {
      await revokeToken(refreshToken);
    }

    sessionStorage.clear();

    await Promise.all([
      StorageService.remove("identityProvider"),
      StorageService.remove("organisationId"),
      clearCachedAuthTokens(),
    ]);

    useUniversalPlayerStore().closeAndStop();
    useSideBarStore().hideSideBar();
    clearPendoSession();

    if (clearAll) {
      await StorageService.remove("login");
    }

    initialState();
    useUserStore().setEnforceTwoFactor(false);

    identityProvider.value = null;

    updateIntercom({
      alignment: "right",
      hide_default_launcher: false,
    });
  }

  async function switchOrganisation(
    switchOrganisationId: number,
    platform: string
  ): Promise<void> {
    const region = userOrganisationsRegion.value;

    if (!region) {
      throw new Error("Organisation region is required for switching");
    }

    let cachedAuthToken = await cachedToken(switchOrganisationId);

    let cachedRefreshTokenValue =
      await cachedRefreshToken(switchOrganisationId);

    if (!cachedAuthToken || !cachedRefreshTokenValue) {
      const accessToken = await currentToken();

      if (!accessToken) {
        throw new Error("Cannot switch organisation without an access token");
      }

      const response = await axios.post<{
        auth_token: string;
        refresh_token: string;
      }>(
        `${identityBaseUrl}/switch`,
        {
          google_cloud_region: region,
          id: switchOrganisationId,
          platform: platform || "desktop",
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const { data } = response;

      cachedAuthToken = data.auth_token;
      cachedRefreshTokenValue = data.refresh_token;

      await Promise.all([
        cacheOrganisationToken(cachedAuthToken, switchOrganisationId),
        cacheOrganisationRefreshToken(
          cachedRefreshTokenValue,
          switchOrganisationId
        ),
      ]);
    }

    await Promise.all([
      StorageService.remove("organisationId", sessionStorage),
      StorageService.set("newOrgId", switchOrganisationId, sessionStorage),
    ]);

    window.location.replace("/");
  }

  function authenticated() {
    loadError.value = null;
  }

  function toSortedOrganisations(
    organisationsPayload: UserOrganisationPayload[]
  ): SortedOrganisation[] {
    return organisationsPayload.map((item) => {
      const itemOrganisation = item.organisation;

      return {
        id: itemOrganisation.id,
        logo_url: itemOrganisation.logo_url || "",
        name: itemOrganisation.name,
        teams: itemOrganisation.teams.length ? itemOrganisation.teams : null,
      };
    });
  }

  async function getUserOrganisations(): Promise<void> {
    const response =
      await $streemApiV1.get<UserOrganisationsResponse>("user_organisations");

    const responseData = response?.data;
    const organisationsPayload = responseData?.organisations || [];

    userOrganisationsRegion.value = responseData?.region || "";

    useUserStore().setUserOrganisations(
      toSortedOrganisations(organisationsPayload)
    );
  }

  function setUserLocale(user: CurrentUser) {
    setLocale(user.locale);
  }

  async function getUser(): Promise<void> {
    const response = await $streemApiV1.get("user");

    const user = response.data;

    currentUser.value = user;
    preferences.value = user.preferences;

    const authToken = await currentToken();
    const refreshToken = await currentRefreshToken();
    const userOrganisationId = user.organisation?.id;

    if (authToken && userOrganisationId) {
      await updateUserToken(authToken, userOrganisationId);
    }

    if (refreshToken && userOrganisationId) {
      await updateUserRefreshToken(refreshToken, userOrganisationId);
    }

    useUserStore().setEnforceTwoFactor(user.enforce_two_factor);
    useUserStore().setUserLocale(user);
    await useUserStore().getUserOrganisations();

    useUserStore().authenticated();
  }

  function adminUserOn(updateAccountManager: AccountManager): void {
    isAdminUser.value = true;

    if (updateAccountManager) {
      accountManager.value = updateAccountManager;
    }
  }

  function adminUserOff(): void {
    isAdminUser.value = false;
  }

  async function adminUserLogin({
    accountManager: newAccountManager,
    refreshToken,
    token: newToken,
  }: {
    accountManager: AccountManager;
    refreshToken: string;
    token: string;
  }): Promise<void> {
    await StorageService.set(
      "accountManager",
      JSON.stringify(newAccountManager),
      sessionStorage
    );

    await updateUserToken(newToken);
    await updateUserRefreshToken(refreshToken);

    useUserStore().adminUserOn(newAccountManager);

    await useUserStore().getUser();

    const permissions = new Permissions();

    if (permissions.has("has_advanced_access")) {
      await newAnalyticsProxy(appInstance, { isAdminUser: true });

      if (LogRocket) {
        LogRocket.identify(String(currentUser.value.id), {
          accountManager: newAccountManager.name,
        });
      }
    } else {
      useUserStore().adminUserOff();
    }

    useUserStore().loginSuccess(newToken);
  }

  function unauthorized(error: unknown): void {
    loadError.value = error;
  }

  async function sudo({
    idToken,
    permission,
    pin,
    provider = "streem",
  }: {
    idToken?: string;
    permission: string;
    pin: string;
    provider?: string;
  }): Promise<boolean> {
    track("Sudo");

    const response = await $streemApiV1.post("sessions/sudo", {
      params: {
        id_token: idToken,
        permission,
        pin,
        provider,
      },
    });

    const result = response.data;
    const authToken = result.auth_token;

    if (authToken) {
      useUserStore().loginSuccess(authToken);

      await updateUserToken(authToken);

      return true;
    }

    return false;
  }

  async function getOrganisation(): Promise<void> {
    const response = await OrganisationsService.show<Organisation>(
      currentUser.value.organisation?.id,
      { handleErrors }
    );

    if (response.data.features) {
      response.data.features = response.data.features.sort();
    }

    organisation.value = response.data;
  }

  async function getMultiplier(): Promise<void> {
    const response = await OrganisationAggregationMultiplier.includes([
      "aveMultiplierLastUpdatedBy",
      "audienceMultiplierLastUpdatedBy",
    ])
      .where({ organisationId: currentUser.value.organisation?.id })
      .all();

    if (response.data.length) {
      [multiplier.value] = response.data;
    }
  }

  async function getTeam(): Promise<void> {
    const response = await $streemApiV1.get("user/team", { handleErrors });

    team.value = response.data;
  }

  async function updateUser(data: UpdateUser): Promise<void> {
    const response = await $streemApiV1.put("user", {
      params: { user: data },
    });

    currentUser.value = response.data;
    useUserStore().setUserLocale(response.data);
    useUserStore().setEnforceTwoFactor(response.data.enforce_two_factor);
  }

  async function updateUserPreferences({
    attribute,
    id,
    value,
  }: {
    attribute: keyof Preferences;
    id: number;
    value: unknown;
  }): Promise<void> {
    applyPreferenceUpdate(attribute, value);

    await UsersService.updatePreferences(id, attribute, value, {
      handleErrors,
    });
  }

  function toggleShowExcerpts(): void {
    showFullExcerpts.value = !showFullExcerpts.value;
  }

  function toggleCustomerView(): void {
    customerViewEnabled.value = !customerViewEnabled.value;
  }

  function toggleBetaFeatures(): void {
    betaEnabled.value = !betaEnabled.value;
  }

  async function refreshGoogleSsoCallback({
    client,
    idToken,
    platform,
  }: {
    client: string;
    idToken: string;
    platform: string;
  }): Promise<void> {
    const response = await $streemApiV1.post("sessions", {
      handleErrors: false,
      params: {
        client,
        id_token: idToken,
        platform,
        provider: "google",
        refresh: true,
        resume: true,
      },
    });

    const authToken: AuthToken = {
      token: response.data.auth_token,
      userId: response.data.user_id,
    };

    await updateUserToken(authToken.token);

    identityProvider.value = "google";
    useUserStore().loginSuccess(authToken);
  }

  async function refreshMicrosoftSsoCallback(
    idToken: string,
    platform?: string
  ): Promise<void> {
    const response = await $streemApiV1.post("sessions", {
      handleErrors: false,
      params: {
        id_token: idToken,
        platform,
        provider: "microsoft",
        refresh: true,
        resume: true,
      },
    });

    const authToken: AuthToken = {
      token: response.data.auth_token,
      userId: response.data.user_id,
    };

    await updateUserToken(authToken.token);

    identityProvider.value = "microsoft";
    useUserStore().loginSuccess(authToken);
  }

  function setOrganisation(newOrganisation: Organisation): void {
    organisation.value = newOrganisation;
  }

  function setUserOrganisations(organisations: SortedOrganisation[]): void {
    userOrganisations.value = organisations;
  }

  function isSuperRole(currentRouteProductMenu: string): boolean {
    if (currentRouteProductMenu === "social") {
      return currentUser.value.social_role === "super";
    }

    if (currentRouteProductMenu === "outreach") {
      return currentUser.value.outreach_role === "super";
    }

    return currentUser.value.role === "super";
  }

  function applyPreferenceUpdate(
    attribute: keyof Preferences,
    value: unknown
  ): void {
    const teamPreferences: (keyof Preferences)[] = [
      "selected_team_media",
      "selected_team_social",
      "selected_team_insights",
      "selected_team_outreach",
    ];

    if (teamPreferences.includes(attribute)) {
      teamPreferences.forEach((key) => {
        preferences.value[key] = value;
      });

      return;
    }

    preferences.value[attribute] = value;
  }

  return {
    accountManager,
    adminUserEnabled,
    adminUserLogin,
    adminUserOff,
    adminUserOn,
    authenticated,
    betaEnabled,
    currentUser,
    customerViewEnabled,
    enforceTwoFactor,
    getMultiplier,
    getOrganisation,
    getTeam,
    getUser,
    getUserOrganisations,
    hasAdvancedAccess,

    hasLaunchpadBetaEnabled,
    identityProvider,
    isAdminUser,
    isLoggedIn,
    isSuperRole,
    loadError,
    login,
    loginSuccess,
    logout,

    multiplier,
    organisation,
    organisationId,
    pending,
    preferences,
    primaryOrganisationTeam,
    refreshGoogleSsoCallback,
    refreshMicrosoftSsoCallback,
    role,
    setEnforceTwoFactor,
    setOrganisation,
    setUserLocale,
    setUserOrganisations,
    showFullExcerpts,
    sortedAllOrganisationTeams,
    sortedOrganisations,
    sortedTeam,
    sortedUserOrganisationTeams,
    sudo,
    switchOrganisation,
    team,
    toggleBetaFeatures,
    toggleCustomerView,
    toggleShowExcerpts,
    token,
    unauthorized,
    updateUser,
    updateUserPreferences,
  };
});

export default useUserStore;
